import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Sparkles, BellRing, Bell, Loader2, Calendar } from 'lucide-react';
import { premieresApi } from '../lib/api.js';
import { useTimezone } from '../hooks/useTimezone.js';
import { useAuthStore, useUiStore, usePlayerStore } from '../lib/stores.js';
import { formatTime } from '../lib/time.js';
import styles from './PremieresPage.module.css';

export default function PremieresPage() {
  const token = useAuthStore((s) => s.token);
  const openModal = useUiStore((s) => s.openModal);
  const navigate = useNavigate();
  const selectChannel = usePlayerStore((s) => s.selectChannel);

  const premieresQ = useQuery({
    queryKey: ['premieres'],
    queryFn: () => premieresApi.list({ page_size: 60 }),
    refetchInterval: 60_000,
  });

  const subsQ = useQuery({
    queryKey: ['premiere-subscriptions'],
    queryFn: premieresApi.subscriptions,
    enabled: !!token,
  });

  const subByPremiereId = useMemo(() => {
    const map = new Map();
    (subsQ.data || []).forEach((s) => map.set(s.premiere.id, s));
    return map;
  }, [subsQ.data]);

  const grouped = useMemo(() => {
    const groups = new Map();
    (premieresQ.data?.results || []).forEach((p) => {
      const d = new Date(p.starts_at);
      const key = d.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    });
    return Array.from(groups.entries());
  }, [premieresQ.data]);

  const qc = useQueryClient();
  const subscribe = useMutation({
    mutationFn: ({ premiere_id, auto_switch }) => premieresApi.subscribe(premiere_id, auto_switch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['premiere-subscriptions'] }),
  });
  const unsubscribe = useMutation({
    mutationFn: (premiere_id) => premieresApi.unsubscribe(premiere_id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['premiere-subscriptions'] }),
  });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1><Sparkles size={22} /> Premieres</h1>
          <p className="muted">Subscribe to a premiere and we'll switch your channel when it goes live.</p>
        </div>
      </header>

      {premieresQ.isLoading && <div className={styles.center}><Loader2 className={styles.spin} /></div>}
      {premieresQ.data?.results?.length === 0 && (
        <div className={styles.empty}>
          <Calendar size={28} />
          <p>No premieres scheduled. Check back later.</p>
        </div>
      )}

      {grouped.map(([day, items]) => (
        <section key={day} className={styles.group}>
          <h2 className={styles.groupTitle}>{day}</h2>
          <div className={styles.grid}>
            {items.map((p) => (
              <PremiereCard
                key={p.id}
                premiere={p}
                subscription={subByPremiereId.get(p.id)}
                onSubscribe={(auto) => {
                  if (!token) { openModal('login'); return; }
                  subscribe.mutate({ premiere_id: p.id, auto_switch: auto });
                }}
                onUnsubscribe={() => unsubscribe.mutate(p.id)}
                onWatchChannel={() => {
                  selectChannel({
                    id: p.channel.id,
                    name: p.channel.name,
                    slug: p.channel.slug,
                    logo_url: p.channel.logo_url,
                  });
                  navigate('/');
                }}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function PremiereCard({ premiere, subscription, onSubscribe, onUnsubscribe, onWatchChannel }) {
  const subscribed = !!subscription;
  const auto = subscription?.auto_switch ?? true;
  const tz = useTimezone();

  return (
    <article className={`${styles.card} ${subscribed ? styles.cardOn : ''}`}>
      <div className={styles.cardHead}>
        <div className={styles.cardLogo}>
          {premiere.channel?.logo_url
            ? <img src={premiere.channel.logo_url} alt="" />
            : <span>{(premiere.channel?.name || '?').slice(0, 2).toUpperCase()}</span>}
        </div>
        <div className={styles.cardChan}>
          <strong>{premiere.channel?.name}</strong>
          <span className={styles.cardTime}>
            {formatTime(premiere.starts_at, tz)}
          </span>
        </div>
        {premiere.is_featured && <span className={styles.badge}>Featured</span>}
      </div>
      <h3 className={styles.cardTitle}>{premiere.title}</h3>
      {premiere.description && <p className={styles.cardDesc}>{premiere.description}</p>}
      <div className={styles.cardActions}>
        {subscribed ? (
          <>
            <button className="btn primary" onClick={onUnsubscribe}>
              <BellRing size={14} /> Subscribed
            </button>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={auto}
                onChange={(e) => onSubscribe(e.target.checked)}
              />
              <span>Auto-switch</span>
            </label>
          </>
        ) : (
          <button className="btn" onClick={() => onSubscribe(true)}>
            <Bell size={14} /> Subscribe
          </button>
        )}
        <button className="btn ghost" onClick={onWatchChannel}>Watch channel</button>
      </div>
    </article>
  );
}
